import { useEffect, useState, useCallback, useRef } from 'react';

export function useInfiniteScroll(
  fetchFn,
  {
    initialPage = 1,
    deps = [],
    responseDataKey,
    searchText,
    scrolledContainerRef,
    handleResponse,
  } = {}
) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [searchHadValue, setSearchHadValue] = useState(false);
  const lastLoadedPage = useRef(null);

  // Track search state changes
  useEffect(() => {
    const hasSearchText =
      searchText && searchText !== '' && !Array.isArray(searchText);

    if (hasSearchText && !searchHadValue) {
      setSearchHadValue(true);
    } else if (hasSearchText) {
      setShowLoading(false);
    }
  }, [searchText, searchHadValue]);

  // Handle filter changes (non-vessel filters)
  useEffect(() => {
    const filterObject = deps?.[1];

    if (filterObject && typeof filterObject === 'object') {
      const hasActiveFilters = Object.entries(filterObject).some(
        ([key, value]) => key !== 'vessel' && value !== null
      );

      if (hasActiveFilters) {
        setShowLoading(true);
        setSearchHadValue(false);
      }
    }
  }, [deps]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || lastLoadedPage.current === page) return;
    lastLoadedPage.current = page;

    // Determine if we should show loading state
    const shouldShowLoading =
      showLoading ||
      !(
        (searchText && searchText !== '' && !Array.isArray(searchText)) ||
        (searchHadValue && searchText === '')
      );

    if (shouldShowLoading) {
      setLoading(true);
    }

    try {
      const response = await fetchFn(page);

      const newData = handleResponse
        ? handleResponse(response)
        : responseDataKey
        ? response.data[responseDataKey].data
        : response.data;

      const isLastPage = responseDataKey
        ? response.data[responseDataKey].last_page === page
        : response.last_page === page;

      // Update items based on whether this is the first page or not
      setItems((prevItems) =>
        page === initialPage ? newData : [...prevItems, ...newData]
      );

      // Update pagination state
      if (isLastPage) {
        setHasMore(false);
      } else if (
        (responseDataKey ? response.data[responseDataKey] : response).data
          .length > 0
      ) {
        setPage((prevPage) => prevPage + 1);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchFn,
    page,
    hasMore,
    loading,
    initialPage,
    responseDataKey,
    searchText,
    showLoading,
    searchHadValue,
  ]);

  // Reset state when dependencies change
  useEffect(() => {
    const shouldClearItems = searchText === '' && !searchHadValue;

    if (shouldClearItems) {
      setItems([]);
    }

    setPage(initialPage);
    setHasMore(true);
    setShowLoading(false);
    lastLoadedPage.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPage, searchHadValue, searchText, ...deps]);

  // Auto-load data when page resets to initial page
  useEffect(() => {
    const shouldAutoLoad = page === initialPage && hasMore && !loading;

    if (shouldAutoLoad) {
      loadMore();
    }
  }, [page, hasMore, loading, loadMore, initialPage]);

  // Auto-load pages if content doesn't fill viewport (for large screens)
  useEffect(() => {
    if (loading || !hasMore || items.length === 0) return;

    const checkAndLoadMore = () => {
      let scrollContainer;
      let scrollHeight;
      let clientHeight;

      if (scrolledContainerRef?.current) {
        scrollContainer = scrolledContainerRef.current;
        scrollHeight = scrollContainer.scrollHeight;
        clientHeight = scrollContainer.clientHeight;
      } else {
        // Use main element instead of window for scroll calculations
        const mainScroll = document.getElementsByTagName('main')[0];
        if (mainScroll) {
          scrollContainer = mainScroll;
          scrollHeight = mainScroll.scrollHeight;
          clientHeight = mainScroll.clientHeight;
        } else {
          // Fallback to document body
          scrollHeight = document.body.scrollHeight;
          clientHeight = window.innerHeight;
        }
      }

      // If content height is less than or equal to viewport height, load more
      // Add a small threshold to account for headers/footers
      const threshold = 100;
      if (scrollHeight <= clientHeight + threshold && hasMore && !loading) {
        loadMore();
      }
    };

    // Use setTimeout to ensure DOM has updated after items change
    const timeoutId = setTimeout(checkAndLoadMore, 100);

    return () => clearTimeout(timeoutId);
  }, [items.length, hasMore, loading, loadMore, scrolledContainerRef]);

  // Handle scroll events for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = 50;
      let isAtBottom = false;

      if (scrolledContainerRef?.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          scrolledContainerRef.current;
        isAtBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;
      } else {
        // Use main element instead of window for scroll calculations
        const mainScroll = document.getElementsByTagName('main')[0];
        if (mainScroll) {
          const { scrollTop, scrollHeight, clientHeight } = mainScroll;
          isAtBottom =
            scrollTop + clientHeight >= scrollHeight - scrollThreshold;
        } else {
          // Fallback to window if main element is not found
          isAtBottom =
            window.innerHeight + window.scrollY >=
            document.body.offsetHeight - scrollThreshold;
        }
      }

      if (isAtBottom && !loading) {
        if (!showLoading) {
          setShowLoading(true);
        }

        loadMore();
      }
    };

    const scrollTarget =
      scrolledContainerRef?.current ||
      document.getElementsByTagName('main')[0] ||
      window;

    scrollTarget.addEventListener('scroll', handleScroll);

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
    };
  }, [loadMore, showLoading, loading, scrolledContainerRef]);

  // Refetch function to manually reload data
  const refetch = useCallback(
    async (options = {}) => {
      const { preservePage = false, targetPage } = options;

      // Determine which page to refetch
      const pageToRefetch = preservePage ? targetPage || page : initialPage;

      // If preserving page, we need to refetch from page 1 up to the target page
      if (preservePage && pageToRefetch > initialPage) {
        // Reset state but keep track of target page
        setLoading(true);
        setShowLoading(false);
        lastLoadedPage.current = null;

        try {
          // Refetch all pages from 1 to targetPage
          let allData = [];
          let lastResponse = null;
          for (let p = initialPage; p <= pageToRefetch; p++) {
            const response = await fetchFn(p);
            lastResponse = response;
            const newData = handleResponse
              ? handleResponse(response)
              : responseDataKey
              ? response.data[responseDataKey].data
              : response.data;

            allData = p === initialPage ? newData : [...allData, ...newData];

            const isLastPage = responseDataKey
              ? response.data[responseDataKey].last_page === p
              : response.last_page === p;

            if (isLastPage) {
              setHasMore(false);
              break;
            }
          }

          setItems(allData);

          // Set page to next page after targetPage if there's more
          if (lastResponse) {
            const lastPage = responseDataKey
              ? lastResponse.data[responseDataKey].last_page
              : lastResponse.last_page;
            if (pageToRefetch < lastPage) {
              setPage(pageToRefetch + 1);
              setHasMore(true);
            } else {
              setPage(pageToRefetch);
            }
          } else {
            setPage(pageToRefetch);
          }
        } catch (error) {
          console.error('Refetch error:', error);
        } finally {
          setLoading(false);
        }
      } else {
        // Original behavior: reset to initial page
        setPage(initialPage);
        setHasMore(true);
        setLoading(false);
        setShowLoading(false);
        lastLoadedPage.current = null;

        // Manually trigger the API call
        try {
          const response = await fetchFn(initialPage);

          const newData = handleResponse
            ? handleResponse(response)
            : responseDataKey
            ? response.data[responseDataKey].data
            : response.data;

          const isLastPage = responseDataKey
            ? response.data[responseDataKey].last_page === initialPage
            : response.last_page === initialPage;

          // Set the new data
          setItems(newData);

          // Update pagination state
          if (isLastPage) {
            setHasMore(false);
          } else {
            setPage((prevPage) => prevPage + 1);
          }
        } catch (error) {
          console.error('Refetch error:', error);
        } finally {
          setLoading(false);
        }
      }
    },
    [fetchFn, initialPage, responseDataKey, handleResponse, page]
  );

  return { items, loading, hasMore, refetch, currentPage: page };
}
